/* "Byg din rutine" (sections/guide.liquid): a few questions per guide, then the result.
   The texts come from the locales through snippets/guide-copy.liquid, so every market language works; this file holds
   only the logic: which questions apply, and which routine (or gift sets) the answers lead to.
   Face, body and hair end in a routine: its section is fetched in guide mode from the product pinned to it
   (sections/productpage-routine.liquid), so the routine discount applies. Gifts end in up to three gift sets.
   The answers are kept in the link (?g=ansigt&concern=linjer,pigment&age=40), so back, reload and sharing work. */

// Gift budgets in the shop's currency; shown and compared in the customer's (Shopify.currency.rate).
const GIFT_BUDGETS = { 300: [0, 300], 500: [300, 500], 800: [500, 800], over: [800, Infinity] };

const GUIDE_LOGIC = {
  ansigt: {
    // Only for lines or firmness at 40+.
    // Only for lines or firmness at 40+, and not for skin that reacts easily (it gets no vitamin A).
    when: {
      // Pregnant or breastfeeding: asked under 55; "Ja" leaves vitamin A and salicylic acid out (owner, 2026-10-20).
      gravid: (a) => a.age !== "55",
      avit: (a) => ["linjer", "slap"].includes(a.concern) && (a.age === "40" || a.age === "55") && a.react !== "ja" && a.gravid !== "ja",
    },
    routine(a) {
      if (a.gravid === "ja") {
        a = { ...a, avit: "nej" };
        // The acne routine has salicylic acid (Acne Serum) and a retinyl clay mask: the mild Skin Balance range instead.
        if (a.concern === "uren") return ["skin-balance", "uren_gravid"];
        if (a.concern === "porer") return ["skin-balance", "porer_gravid"];
        // The firming routine's day cream has vitamin A (Firming Cream SPF 50): the moisture and collagen routine instead.
        if (a.concern === "slap") return ["fugt-og-kollagen", "slap_gravid"];
      }
      const young = a.age === "u25" || a.react === "ja";
      // "Reagerer din hud let?" promises the mildest care without vitamin A and acids, on every branch (owner's review,
      // 2026-10-20: rough skin still got the scrub routine): the gentle routine for lines, dryness and rough skin, and a
      // vitamin A answer from before is not used.
      if (a.react === "ja" && ["linjer", "tor", "ru"].includes(a.concern)) return ["tor-og-sensitiv-hud", "tor_react"];
      if (a.react === "ja") a = { ...a, avit: "nej" };
      switch (a.concern) {
        case "tor":
          // Oily or T-zone skin that feels tight lacks moisture, not oil: the light routine with a gel cleanser, not the
          // dry routine's balm (decided 2026-10-20 after the merchandising review).
          if (a.feel === "fedtet") return ["fugt-og-kollagen", "tor_fedtet"];
          if (a.feel === "tzone") return ["fugt-og-kollagen", "tor_tzone"];
          return ["tor-og-sensitiv-hud", "tor"];
        case "linjer":
          return a.avit === "ja" ? ["fornyelse", "linjer_avit"] : ["fugt-og-kollagen", "linjer"];
        case "slap":
          return a.avit === "ja" ? ["firming-olie", "slap_avit"] : ["firming", "slap"];
        case "pigment":
          return ["pigment", "pigment"];
        case "uren":
          return young ? ["skin-balance", "uren_ung"] : ["acne", "uren"];
        case "porer":
          return young ? ["skin-balance", "porer_ung"] : ["acne", "porer"];
        case "rodme":
          return ["anti-rodme", "rodme"];
        case "mat":
          return a.feel === "tor" ? ["facial-oil", "mat_tor"] : ["glod-og-beskyttelse", "mat"];
        case "hudtone":
          // The pigment routine: niacinamide and vitamin C, a day cream with SPF 30 in Enkel and a night cream in Komplet
          // (routine review, 2026-10-21; before: the tinted cream's routine, then Glød og beskyttelse, which lacked both).
          return ["pigment", "hudtone"];
        case "ru":
          return ["rensescrub", "ru"];
      }
      return null;
    },
    // "Til ham" on the first question: the men's routine straight away.
    links: { concern: [{ key: "herre", routine: "herre" }, { key: "hub" }] },
  },
  krop: {
    when: { texture: (a) => a.concern === "tor" },
    routine(a) {
      // Rough skin's routine treats with an acid lotion: skin that reacts easily gets the moisture routine instead.
      if (a.concern === "ru" && a.react === "ja") return ["blod-krop", "krop_ru_react"];
      // Blemishes and pigmentation are treated with the acid lotion too: reactive skin gets the moisture routine.
      if ((a.concern === "uren" || a.concern === "pigment") && a.react === "ja") return ["blod-krop", "krop_mild"];
      switch (a.concern) {
        case "tor":
          return a.texture === "olie" ? ["kropsolie", "krop_olie"] : ["blod-krop", "krop_lotion"];
        case "ru":
          return ["glat-krop", "krop_ru"];
        // Owner 2026-10-22: blemishes and pigmentation follow the acid lotion's routine (Bodycare Scrub with charcoal,
        // AHA Body Repair with niacinamide, sunscreen); fine lines and firmness the body lotion's.
        case "uren":
          return ["glat-krop", "krop_uren"];
        case "pigment":
          return ["glat-krop", "krop_pigment"];
        case "linjer":
          return ["blod-krop", "krop_linjer"];
        case "haender":
          return ["haender-og-foedder", "haender"];
        case "fodder":
          return ["fodder", "fodder"];
        case "sol":
          return ["sol", "sol"];
        case "selvbruner":
          return ["selvbruner-krop", "selvbruner"];
      }
      return null;
    },
    links: { concern: [{ key: "hub" }] },
  },
  haar: {
    routine(a) {
      // Dandruff or a dry, itchy scalp needs a scalp shampoo first, whatever the hair needs (owner, 2026-10-21).
      if (a.concern !== "hovedbund" && a.scalp === "skael") return ["hovedbund", "haar_skael"];
      if (a.concern !== "hovedbund" && a.scalp === "tor") return ["hovedbund", "haar_tor_hovedbund"];
      return {
        tort: ["pleje-til-tort-haar", "haar_tort"],
        slidt: ["pleje-til-tort-haar", "haar_slidt"],
        fint: ["volumen", "haar_fint"],
        farvet: ["pleje-til-tort-haar", "haar_farvet"],
        hovedbund: ["hovedbund", "haar_hovedbund"],
      }[a.concern] || null;
    },
    links: { concern: [{ key: "hub" }] },
  },
  gave: {
    when: { tema: (a) => a.til !== "ham" },
  },
};

// Answers a saved routine never holds (owner, 2026-10-26: pregnancy is never stored, and the vitamin A question is only asked
// after "no"). A saved routine opened with gemt=1 skips them: its result comes from the saved routine and products.
const UNSAVED_ANSWERS = new Set(["gravid", "avit"]);
// Not answers: the marker of a saved routine, and the result's own choices (Enkel/Komplet, scent).
const NOT_ANSWERS = new Set(["gemt", "ret", "koeb", "niveau", "duft", "view", "preview_theme_id"]);

// Not the guide's at all, kept in the address as they came: the theme preview, and tracking (Google's _gl, which it adds
// on the way from the account pages, utm_*, click ids). Read as answers they would make a saved routine stop matching.
const CLICK_IDS = new Set(["gclid", "gbraid", "wbraid", "dclid", "fbclid", "msclkid", "ttclid", "igshid", "srsltid", "mc_cid", "mc_eid"]);
const NOT_GUIDE = (key) => key === "view" || key === "preview_theme_id" || key.startsWith("_") || key.startsWith("utm_") || CLICK_IDS.has(key);

// "{{ name }}" placeholders arrive as [name] from the locales.
const fill = (text, values) => String(text || "").replace(/\[(\w+)\]/g, (m, key) => (key in values ? values[key] : m));

class GuideFlow extends HTMLElement {
  connectedCallback() {
    this.hub = this.querySelector("[data-guide-hub]");
    this.flow = this.querySelector("[data-guide-flow]");
    this.result = this.querySelector("[data-guide-result]");
    // The t filter escapes HTML ("&" arrives as "&amp;"); the texts go in as plain text, so they are decoded once here.
    const decode = (value) => {
      if (typeof value === "string") return value.includes("&") ? new DOMParser().parseFromString(value, "text/html").documentElement.textContent : value;
      if (Array.isArray(value)) return value.map(decode);
      if (value && typeof value === "object") return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, decode(v)]));
      return value;
    };
    this.copy = decode(JSON.parse(this.querySelector("[data-guide-copy]").textContent));
    const routines = this.querySelector("[data-guide-routines]");
    this.routines = routines ? JSON.parse(routines.textContent) : {};
    this.linkLabels = JSON.parse(this.dataset.linkLabels || "{}");
    // The logged-in customer's saved routines (sections/guide.liquid; none when logged out).
    const saved = this.querySelector("[data-guide-saved]");
    try {
      this.saved = saved ? JSON.parse(saved.textContent) || [] : [];
    } catch (e) {
      this.saved = [];
    }
    // In the panel over a collection (guide-drawer) the answers stay here, not in the page's address.
    this.host = this.closest("guide-drawer");
    if (this.host) this.memory = { ...(this.host.startState || { g: this.host.dataset.start }) };

    this.querySelectorAll("[data-guide-start]").forEach((button) =>
      button.addEventListener("click", () => this.go({ g: button.dataset.guideStart }))
    );
    this.querySelector("[data-guide-back]").addEventListener("click", () => this.go(this.backState || {}));
    // In the panel "Start forfra" starts this guide again; the hub belongs to the guide's own page.
    // "Tag testen igen" starts this test again, on its page too (owner, 2026-10-22: it went to the overview).
    this.querySelector("[data-guide-restart]").addEventListener("click", () => this.go({ g: this.state().g }));
    this.doubtButton = this.querySelector("[data-guide-doubt]");
    this.doubtText = this.querySelector("[data-guide-doubt-text]");
    // "Hjælp til at svare" opens the theme's drawer; the answers stay where they are.
    this.helpDrawer = this.querySelector("[data-guide-help-drawer]");
    // In the collection panel the drawer would open inside the panel (its transform holds fixed boxes), half wide with a
    // gap beside it (owner, 2026-10-21): it moves to the page, over the panel. An earlier copy from a reload goes first.
    if (this.host && this.helpDrawer) {
      const old = document.body.querySelector(`:scope > #${CSS.escape(this.helpDrawer.id)}`);
      if (old && old !== this.helpDrawer) old.remove();
      document.body.append(this.helpDrawer);
    }
    this.doubtButton.setAttribute("aria-haspopup", "dialog");
    this.doubtButton.removeAttribute("aria-expanded");
    this.doubtButton.addEventListener("click", () => this.helpDrawer && this.helpDrawer.openDrawer && this.helpDrawer.openDrawer());
    this.nextButton = this.querySelector("[data-guide-next]");
    // "Ret" on one answer of the result: that question alone, then straight back to the result (ret=<question> in the link).
    this.editBack = this.querySelector("[data-guide-edit-back]");
    if (this.editBack) this.editBack.addEventListener("click", () => {
      const back = { ...this.state() };
      delete back.ret;
      this.go(back);
    });
    this.nextButton.addEventListener("click", () => this.nextAction && this.nextAction());
    ["pointerenter", "touchstart", "focus"].forEach((type) => this.nextButton.addEventListener(type, () => this.nextPrefetch && this.nextPrefetch(), { passive: true }));
    // A product in the routine result opens in place (its details), not on its own page.
    this.peek = this.querySelector("[data-guide-peek]");
    this.querySelector("[data-guide-routine]").addEventListener("click", (event) => {
      const link = event.target.closest("a[href*='/products/']");
      if (!link || event.metaKey || event.ctrlKey || event.shiftKey) return;
      event.preventDefault();
      this.openPeek(link.getAttribute("href"));
    });
    window.ec.prefetchWithin(this.querySelector("[data-guide-routine]"), "a[href*='/products/']", (link) => [link.getAttribute("href"), "product-peek"]);
    this.querySelector("[data-guide-peek-back]").addEventListener("click", () => this.closePeek());
    this.peek.addEventListener("click", (event) => {
      if (event.target === this.peek) this.closePeek();
    });
    // "Gem rutine" (snippets/account-save.liquid): to the customer's account, or login and back here when logged out.
    this.saveButton = this.querySelector("[data-account-save]");
    if (this.saveButton) this.saveButton.addEventListener("click", () => window.ec.saveRoutine(this.saveButton, this.savePayload(), this.resultUrl(this.state())));
    // In the panel over a collection the title's line has the close button too: "Gem rutine" (and its "saved" link) moves
    // under the routine, a step after buying (owner, 2026-10-28: too many things on that line).
    // "Tilpas butikken til mig" (sections/guide.liquid): the save button is its action, on the page and in the panel alike.
    const rememberSlot = this.querySelector("[data-guide-remember-action]");
    if (rememberSlot && this.saveButton) {
      const view = this.saveButton.nextElementSibling && this.saveButton.nextElementSibling.matches("[data-account-save-view]") ? this.saveButton.nextElementSibling : null;
      rememberSlot.append(this.saveButton);
      if (view) rememberSlot.append(view);
    } else if (this.querySelector("[data-guide-remember]")) {
      this.querySelector("[data-guide-remember]").hidden = true;
    }
    if (this.host && this.saveButton && !rememberSlot) {
      const after = this.querySelector(".guide__after");
      const view = this.saveButton.nextElementSibling && this.saveButton.nextElementSibling.matches("[data-account-save-view]") ? this.saveButton.nextElementSibling : null;
      if (after) {
        const row = Object.assign(document.createElement("div"), { className: "guide__save-row" });
        row.append(this.saveButton);
        if (view) row.append(view);
        after.prepend(row);
      }
    }
    this.querySelector("[data-guide-share]").addEventListener("click", () => this.share());
    if (!this.host) window.addEventListener("popstate", () => this.render());
    // "Køb rutinen" from Mit ECOOKING (gemt=1&koeb=1): the result goes in the cart once it has loaded (buySaved).
    this.buyPending = !this.host && window.ec.takeBuy && window.ec.takeBuy();
    this.render();
  }

  // The state is the link: g (the guide), the answers by question id, and r for a routine chosen directly ("Til ham").
  state() {
    if (this.host) return { ...this.memory };
    const state = {};
    new URLSearchParams(window.location.search).forEach((value, key) => {
      if (!NOT_GUIDE(key)) state[key] = value;
    });
    return state;
  }

  go(state) {
    if (this.host) {
      this.memory = { ...state };
      return this.render(true);
    }
    const params = new URLSearchParams(window.location.search);
    [...params.keys()].forEach((key) => {
      if (!NOT_GUIDE(key)) params.delete(key);
    });
    Object.entries(state).forEach(([key, value]) => params.set(key, value));
    const query = params.toString();
    window.history.pushState(null, "", window.location.pathname + (query ? "?" + query : ""));
    this.render(true);
  }

  // The answers the rules read: the first concern is the one the routine is built on.
  answers(state) {
    const answers = { ...state };
    if (answers.concern) answers.concern = answers.concern.split(",")[0];
    return answers;
  }

  // The questions that apply to these answers, in order.
  questions(id, state) {
    const when = (GUIDE_LOGIC[id] && GUIDE_LOGIC[id].when) || {};
    const answers = this.answers(state);
    return this.copy.guides[id].questions.filter((q) => !when[q.id] || when[q.id](answers));
  }

  // How many questions this path can still have: a question that hangs on answers not given yet counts while some
  // answer could still bring it, so the count reads neither "1/1, 2/2" nor a question that can't come (owner, 2026-10-19).
  total(id, state) {
    const when = (GUIDE_LOGIC[id] && GUIDE_LOGIC[id].when) || {};
    const all = this.copy.guides[id].questions;
    const possible = (q, i) => {
      if (!when[q.id]) return true;
      const open = all.slice(0, i).filter((p) => !state[p.id]);
      // Every combination of the open answers (a handful of options each), until one brings the question.
      const tryFrom = (k, draft) =>
        k === open.length ? when[q.id](this.answers(draft)) : open[k].options.some((o) => tryFrom(k + 1, { ...draft, [open[k].id]: o.value }));
      return tryFrom(0, { ...state });
    };
    return all.filter(possible).length;
  }

  render(moved) {
    if (this.peek && this.peek.open) this.peek.close();
    const state = this.state();
    const guide = this.copy.guides[state.g];
    this.hub.hidden = !!guide;
    this.flow.hidden = true;
    this.result.hidden = true;
    if (!guide) {
      this.buyPending = false;
      this.classList.remove("is-deep");
      if (moved && this.host) this.host.scrollTop();
      else if (moved) this.scrollIntoView({ block: "start" });
      return;
    }
    const questions = this.questions(state.g, state);
    // Editing one answer from the result ("Ret"): that question, with the answer(s) chosen.
    const editing = state.ret && !state.r ? questions.find((q) => q.id === state.ret && state[q.id]) : null;
    let open = state.r ? null : editing || questions.find((q) => !state[q.id]);
    // A saved routine (gemt=1, one of the customer's own): straight to its result. Only the answers it never keeps
    // (pregnancy, vitamin A) may be missing; they are not asked, and the result is the saved routine and products.
    this.savedRoutine = null;
    if (open && !editing && UNSAVED_ANSWERS.has(open.id) && questions.every((q) => state[q.id] || UNSAVED_ANSWERS.has(q.id))) {
      this.savedRoutine = this.savedMatch(state);
      if (this.savedRoutine) open = null;
    }
    // Køb rutinen only for the result it was opened on (not one reached later by answering).
    if (open) this.buyPending = false;
    // The result holds a screen's height while its routine loads (section-guide.css), so nothing below it jumps.
    this.classList.toggle("is-deep", !open);
    if (open) this.showQuestion(state, questions, open);
    else this.showResult(state, questions);
    if (!open && this.analyse) this.runAnalyse(state);
    this.analyse = false;
    if (moved && this.host) {
      this.host.scrollTop();
      const focus = (open ? this.flow : this.result).querySelector("[tabindex='-1']");
      if (focus) focus.focus({ preventScroll: true });
    } else if (moved) {
      const target = open ? this.flow : this.result;
      // The theme scrolls the body, not the window: scrollIntoView, just under the pinned header.
      const header = parseFloat(getComputedStyle(document.documentElement).getPropertyValue("--nav-height")) || 0;
      if (target.getBoundingClientRect().top < header) {
        target.style.scrollMarginTop = header + 16 + "px";
        target.scrollIntoView({ block: "start" });
      }
      const focus = target.querySelector("[tabindex='-1']");
      if (focus) focus.focus({ preventScroll: true });
    }
  }

  // The answers before this question, as a state to go to.
  before(state, questions, question) {
    const next = { g: state.g };
    questions.slice(0, questions.indexOf(question)).forEach((q) => {
      if (state[q.id]) next[q.id] = state[q.id];
    });
    return next;
  }

  optionLabel(question, option) {
    if (option.label) return option.label;
    if (question.id === "budget") {
      const [from, to] = GIFT_BUDGETS[option.value];
      const ui = this.copy.ui;
      if (from === 0) return fill(ui.budget_under, { amount: this.money(to) });
      if (to === Infinity) return fill(ui.budget_over, { amount: this.money(from) });
      return fill(ui.budget_between, { from: this.money(from, true), to: this.money(to) });
    }
    return option.value;
  }

  rate() {
    return Number((window.Shopify && Shopify.currency && Shopify.currency.rate) || 1);
  }

  // A budget in the shop's currency, converted to the customer's and rounded to a round number (ec-shared.js formats).
  money(amount, bare) {
    let value = amount * this.rate();
    const step = value >= 100 ? 50 : value >= 20 ? 5 : 1;
    value = Math.round(value / step) * step;
    return window.ec.money(value * 100, true, bare);
  }

  showQuestion(state, questions, question) {
    // A question (Ret, or the guide again) ends a save still waiting for the result to load after the login.
    this.saveAfterLogin = false;
    const index = questions.indexOf(question);
    const ui = this.copy.ui;
    const guide = this.copy.guides[state.g];
    // Ret from the result: no way through the questions, just "Tilbage til resultatet" and a button that shows the new result.
    const edit = state.ret === question.id;
    this.backState = index > 0 ? this.before(state, questions, questions[index - 1]) : {};
    // In the panel there is nothing before the first question (the close button is right there).
    this.querySelector("[data-guide-back]").hidden = edit || (!!this.host && index === 0);
    this.querySelector(".guide__progress").hidden = edit;
    if (this.editBack) this.editBack.hidden = !edit;
    const nextLabel = this.nextButton.querySelector("span");
    if (nextLabel) {
      if (!this.nextButton.dataset.label) this.nextButton.dataset.label = nextLabel.textContent;
      nextLabel.textContent = edit && this.nextButton.dataset.labelEdit ? this.nextButton.dataset.labelEdit : this.nextButton.dataset.label;
    }
    this.flow.hidden = false;
    const total = Math.max(questions.length, this.total(state.g, state));
    // The count is for screen readers only: the path branches, so a visible "n/total" changed its total mid-way
    // (owner, 2026-10-19). The bar never moves back when going forward.
    this.flow.querySelector("[data-guide-count]").textContent = fill(ui.count, { guide: guide.title, n: index + 1, total });
    let share = ((index + 1) / total) * 100;
    const last = this.progressAt;
    if (last && last.g === state.g && index > last.index) share = Math.max(share, last.share);
    this.progressAt = { g: state.g, index, share };
    this.flow.querySelector("[data-guide-progress]").style.width = share + "%";
    this.flow.querySelector("[data-guide-question]").textContent = question.text;
    const help = this.flow.querySelector("[data-guide-help]");
    help.textContent = question.help || "";
    help.hidden = !question.help;
    // The (i): why we ask, and a tip for whoever is unsure – whichever the question has.
    const parts = [
      [ui.why_ask, question.why],
      [ui.unsure, question.doubt],
    ].filter(([, text]) => text);
    // What each answer means (skin types, concerns): one line per answer under its name (The Ordinary's "Identifying Skin Types").
    const described = question.options.filter((o) => o.desc);
    this.doubtButton.hidden = !parts.length && !described.length;
    this.doubtText.replaceChildren(
      ...parts.map(([label, text]) => {
        const p = document.createElement("p");
        const strong = document.createElement("strong");
        strong.textContent = label;
        p.append(strong, document.createElement("br"), text);
        return p;
      }),
      ...(described.length
        ? [
            Object.assign(document.createElement("p"), { className: "guide__options-guide-title" }),
            (() => {
              const dl = document.createElement("dl");
              dl.className = "guide__options-guide";
              described.forEach((o) => {
                const dt = document.createElement("dt");
                dt.textContent = this.optionLabel(question, o);
                const dd = document.createElement("dd");
                dd.textContent = o.desc;
                dl.append(dt, dd);
              });
              return dl;
            })(),
          ]
        : [])
    );
    const guideTitle = this.doubtText.querySelector(".guide__options-guide-title");
    if (guideTitle) guideTitle.append(Object.assign(document.createElement("strong"), { textContent: ui.option_guide }));

    // A tap chooses, "Næste" moves on, on every question (owner, 2026-10-21: a mis-tap shouldn't move on by itself). A
    // question that takes more (max) collects them in order. Back or "Ret" shows the earlier answer chosen.
    const max = question.max || 1;
    this.lastAnswers = this.lastAnswers || {};
    const earlier = state[question.id] || this.lastAnswers[state.g + ":" + question.id] || "";
    let picked = earlier ? earlier.split(",").slice(0, max) : [];
    const options = this.flow.querySelector("[data-guide-options]");
    options.setAttribute("aria-label", question.text);
    const proceed = (value) => {
      this.lastAnswers[state.g + ":" + question.id] = value;
      if (edit) {
        // The other answers stay; an answer whose question no longer applies goes, and a question the change brings in
        // is asked next (render asks the first open one). Not a saved routine any more (gemt goes).
        const next = { ...state, [question.id]: value };
        delete next.ret;
        delete next.gemt;
        const applies = new Set(this.questions(state.g, next).map((q) => q.id));
        this.copy.guides[state.g].questions.forEach((q) => {
          if (!applies.has(q.id)) delete next[q.id];
        });
        this.analyse = false;
        return this.go(next);
      }
      const next = { ...this.before(state, questions, question), [question.id]: value };
      // Answers after this one that the link still holds (a question brought in by "Ret" asks only itself): kept while
      // their question still applies, with the scent chosen on the result. On the normal way through there are none.
      const later = { ...next };
      questions.slice(index + 1).forEach((q) => {
        if (state[q.id]) later[q.id] = state[q.id];
      });
      const still = new Set(this.questions(state.g, later).map((q) => q.id));
      questions.slice(index + 1).forEach((q) => {
        if (state[q.id] && still.has(q.id)) next[q.id] = state[q.id];
      });
      if (state.duft && questions.slice(index + 1).some((q) => state[q.id])) next.duft = state.duft;
      // The last answer of a routine guide: a short "we're analysing" moment before the result (owner, 2026-10-21,
      // after Skinome's test). Only on the way in; a result opened from a link or "Ret" shows at once.
      this.analyse = state.g !== "gave" && this.questions(state.g, next).every((q) => next[q.id]);
      this.go(next);
    };
    const paint = () => {
      options.querySelectorAll(".answer").forEach((button) => {
        const at = picked.indexOf(button.dataset.value);
        button.setAttribute("aria-pressed", String(at > -1));
        // Several answers: the order they were tapped in is their priority, shown as 1, 2, 3 (the first decides the routine).
        const order = button.querySelector(".answer__order");
        if (order) order.textContent = at > -1 ? String(at + 1) : "";
      });
      this.nextButton.closest("[data-guide-foot]").hidden = false;
      this.nextButton.disabled = !picked.length;
    };
    options.replaceChildren(
      ...question.options.map((option) => {
        const button = document.createElement("button");
        button.type = "button";
        button.className = "answer";
        button.dataset.value = option.value;
        const label = document.createElement("span");
        label.textContent = this.optionLabel(question, option);
        button.append(label);
        // A question that takes more than one answer ticks each chosen one (component-answer.css).
        if (max > 1) button.insertAdjacentHTML("beforeend", '<span class="answer__order" aria-hidden="true"></span>');
        if (max === 1) ["pointerenter", "touchstart", "focus"].forEach((type) => button.addEventListener(type, () => this.prefetchResult(state, questions, question, option.value), { passive: true }));
        button.addEventListener("click", () => {
          if (max === 1) {
            picked = [option.value];
            paint();
            return;
          }
          const at = picked.indexOf(option.value);
          if (at > -1) picked.splice(at, 1);
          else if (picked.length < max) picked.push(option.value);
          else picked = [...picked.slice(0, max - 1), option.value];
          paint();
        });
        return button;
      })
    );
    this.nextAction = () => picked.length && proceed(picked.join(","));
    this.nextPrefetch = () => picked.length && this.prefetchResult(state, questions, question, picked.join(","));
    paint();

    let links = ((GUIDE_LOGIC[state.g] || {}).links || {})[question.id] || [];
    // In the panel the hub link becomes direct links to the other routine guides.
    if (this.host) {
      links = links.flatMap((link) =>
        link.key !== "hub" ? [link] : ["ansigt", "krop", "haar"].filter((g) => g !== state.g).map((g) => ({ key: "guide_" + g, guide: g }))
      );
    }
    this.flow.querySelector("[data-guide-other]").hidden = edit || !links.length;
    this.flow.querySelector("[data-guide-links]").replaceChildren(
      ...links.map((link) => {
        const button = document.createElement("button");
        button.type = "button";
        button.className = "guide__link";
        button.textContent = link.guide ? this.copy.title[link.guide] : this.linkLabels[link.key + "_" + state.g] || this.linkLabels[link.key] || link.key;
        button.addEventListener("click", () => this.go(link.routine ? { g: state.g, r: link.routine } : link.guide ? { g: link.guide } : {}));
        return button;
      })
    );
  }

  // Three lines in turn (2 s each; owner, 2026-10-21: longer, twice) over the result while it loads: the answers,
  // the main concern, the routine. It ends once the routine is there (at most 9 s); with reduced motion it is one short line without the spinner.
  runAnalyse(state) {
    const ui = this.copy.ui;
    // The concerns in the customer's order: the first, then the second and third together (owner, 2026-10-21: "so the
    // customer believes it matches all needs").
    const nouns = state.g === "ansigt" && state.concern ? state.concern.split(",").map((k) => (this.copy.for.concern || {})[k]).filter(Boolean) : [];
    const lines = [ui.analyse_1, nouns.length ? fill(ui.analyse_2, { concern: nouns[0] }) : ui.analyse_2_any];
    if (nouns.length > 1) lines.push(fill(ui.analyse_2_more, { concerns: this.joinList(nouns.slice(1)) }));
    lines.push(ui.analyse_3);
    const calm = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let box = this.querySelector("[data-guide-analyse]");
    if (!box) {
      box = document.createElement("div");
      box.className = "guide__analyse";
      box.setAttribute("data-guide-analyse", "");
      box.setAttribute("role", "status");
      box.innerHTML = '<span class="guide__analyse-spinner" aria-hidden="true"></span><p class="guide__analyse-text"></p>';
      this.result.before(box);
    }
    const text = box.querySelector(".guide__analyse-text");
    clearInterval(this.analyseTimer);
    let i = 0;
    text.textContent = lines[0];
    this.classList.add("is-analysing");
    box.hidden = false;
    const started = Date.now();
    const step = lines.length > 3 ? 1800 : 2000;
    const min = calm ? 900 : step * lines.length;
    this.analyseTimer = setInterval(() => {
      const routine = this.result.querySelector("[data-guide-routine]");
      const ready = !routine || routine.dataset.loaded || routine.querySelector(".guide__error");
      const spent = Date.now() - started;
      if ((spent >= min && ready) || spent >= min + 3000) {
        clearInterval(this.analyseTimer);
        box.hidden = true;
        this.classList.remove("is-analysing");
        const title = this.result.querySelector("[tabindex='-1']");
        if (title) title.focus({ preventScroll: true });
        return;
      }
      if (!calm && i < lines.length - 1 && spent >= (i + 1) * step) text.textContent = lines[++i];
    }, 150);
  }

  // The last question: the routine an answer leads to starts loading as soon as the answer is touched or pointed at, so
  // the result is there when it opens.
  prefetchResult(state, questions, question, value) {
    if (state.g === "gave" || !GUIDE_LOGIC[state.g]) return;
    const next = { ...this.before(state, questions, question), [question.id]: value };
    if (this.questions(state.g, next).some((q) => !next[q.id])) return;
    const [handle] = GUIDE_LOGIC[state.g].routine(this.answers(next)) || [];
    const routine = handle && this.routines[handle];
    if (routine) window.ec.sectionFetch(routine.url, "productpage-routine").catch(() => {});
  }

  showResult(state, questions) {
    const ui = this.copy.ui;
    this.result.hidden = false;
    // A face routine has the Enkel/Komplet switch: its place is kept while the routine loads (section-guide.css).
    this.result.classList.toggle("is-face", state.g === "ansigt" && !state.r);
    const title = this.result.querySelector("[data-guide-result-title]");
    const explain = this.result.querySelector("[data-guide-explain]");
    const also = this.result.querySelector("[data-guide-also]");
    explain.hidden = true;
    also.hidden = true;
    this.result.querySelector("[data-guide-level-text]").hidden = true;

    // The answers, each with "Ret" back to its question (keeping the answers before it).
    // Its own marker: the routine's lists carry data-guide-answers (the order tag, tagRoutine), and the answers now come
    // after the routine, so the old selector found the routine's list and replaced it on a second result (owner, 2026-10-20).
    const answerList = this.result.querySelector("[data-guide-answer-list]");
    answerList.replaceChildren(
      ...(state.r ? [] : questions.filter((q) => state[q.id])).flatMap((question) =>
        state[question.id].split(",").map((value) => {
          const option = question.options.find((o) => o.value === value);
          const item = document.createElement("li");
          item.className = "guide__answer";
          const tick = document.createElementNS("http://www.w3.org/2000/svg", "svg");
          tick.setAttribute("viewBox", "0 0 20 20");
          tick.setAttribute("aria-hidden", "true");
          const use = document.createElementNS("http://www.w3.org/2000/svg", "use");
          use.setAttribute("href", "#icon-checkmark");
          tick.append(use);
          const text = document.createElement("span");
          text.textContent = option ? this.optionLabel(question, option) : value;
          const change = document.createElement("button");
          change.type = "button";
          change.className = "guide__change txt-btn-underscore-1";
          change.textContent = ui.change;
          change.setAttribute("aria-label", fill(ui.change_label, { question: question.text }));
          // That question alone, its answer chosen; the other answers stay (owner, 2026-10-27: Ret started it over).
          change.addEventListener("click", () => this.go({ ...state, ret: question.id }));
          item.append(tick, text, change);
          return item;
        })
      )
    );

    // Folded into one line under the routine: "Dine svar  Ru hud, Tør, stram hud …" (open it to change one).
    const box = this.result.querySelector("[data-guide-answers-box]");
    if (box) {
      box.hidden = !answerList.children.length;
      box.open = false;
      box.querySelector("[data-guide-answers-text]").textContent = [...answerList.children].map((li) => li.querySelector("span").textContent).join(", ");
    }

    const routineBox = this.result.querySelector("[data-guide-routine]");
    const gifts = this.result.querySelector("[data-guide-gifts]");
    const note = this.result.querySelector("[data-guide-gift-note]");
    gifts.hidden = true;
    note.hidden = true;

    if (state.g === "gave") {
      routineBox.replaceChildren();
      delete routineBox.dataset.loaded;
      title.textContent = ui.result_gift;
      this.renderPromise(routineBox);
      this.showGifts(state, gifts, note);
      this.markCurrent(state, title.textContent);
      return;
    }

    const logic = GUIDE_LOGIC[state.g];
    const answers = this.answers(state);
    const herre = state.r === "herre";
    let [handle, branch] = herre ? ["herre", "herre"] : logic.routine(answers) || [];
    // A saved routine: its own routine, or (none saved, after a pregnancy answer) the one its saved products come from.
    if (this.savedRoutine && !herre) {
      const choice = this.savedChoice(state, this.savedRoutine);
      if (!choice) return;
      [handle, branch] = choice;
    }
    const goals = this.copy.goal[state.g] || {};
    // Hair sent to the scalp routine (dandruff, a dry scalp): the title says what the routine is for.
    const goalKey = branch === "haar_skael" || branch === "haar_tor_hovedbund" ? "hovedbund" : answers.concern;
    title.textContent = herre ? ui.result_herre : fill(ui.result_routine, { goal: goals[goalKey] || "" });
    // What the routine is for comes from the answers alone: shown with the title, not when the routine arrives (CLS).
    this.renderFor(state);
    const why = this.result.querySelector("[data-guide-why]");
    if (why) {
      why.open = false;
      why.hidden = !this.copy.explain[branch];
    }
    if (this.copy.explain[branch]) {
      explain.textContent = this.copy.explain[branch];
      explain.hidden = false;
    }

    // The second concern was said under the routine ("Rutinen hjælper også mod …"); the numbered concerns at the top
    // and "Hvorfor denne rutine?" say it already (owner, 2026-10-22), so the line is gone.
    const second = false;
    if (second && goals[second]) {
      const [otherHandle] = logic.routine({ ...answers, concern: second }) || [];
      const text = also.querySelector("[data-guide-also-text]");
      const link = also.querySelector("[data-guide-also-link]");
      // Only when the routine covers the second concern too; never a second routine to choose (owner, 2026-10-20: the
      // result is the one routine made for you).
      if (otherHandle === handle) {
        text.textContent = fill(ui.also_same, { goal: goals[second] });
        link.hidden = true;
        also.hidden = false;
      }
    }

    this.currentRoutine = handle || "";
    this.markCurrent(state, title.textContent);
    const tagged = Object.entries(state).filter(([key]) => key !== "gemt" && key !== "ret");
    this.stateTag = tagged.map(([key, value]) => key + "=" + value).join("&");
    // The cart's _guide tag never holds the pregnancy or vitamin A answer: it is stored on the order line (owner, 2026-10-26).
    this.answersTag = tagged.filter(([key]) => !UNSAVED_ANSWERS.has(key)).map(([key, value]) => key + "=" + value).join("&");
    this.loadRoutine(this.routines[handle], routineBox);
    this.tagRoutine(routineBox);
  }

  // Three short lines over the routine: what it works on, why it is safe to try, and the ritual it gives you.
  renderPromise(box) {
    const list = this.querySelector("[data-guide-promise]");
    const builder = box.querySelector("routine-builder");
    if (!list) return;
    if (!builder || this.state().g === "gave") {
      list.hidden = true;
      return;
    }
    const ui = this.copy.ui;
    const state = this.state();
    const ritual = { both: ui.promise_ritual_both, morning: ui.promise_ritual_morning, evening: ui.promise_ritual_evening }[builder.dataset.ritual] || ui.promise_ritual_any;
    const tick = () => {
      const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
      svg.setAttribute("viewBox", "0 0 20 20");
      svg.setAttribute("aria-hidden", "true");
      const use = document.createElementNS("http://www.w3.org/2000/svg", "use");
      use.setAttribute("href", "#icon-checkmark");
      svg.append(use);
      return svg;
    };
    const line = (...parts) => {
      const li = document.createElement("li");
      li.append(tick(), ...parts);
      return li;
    };
    // Reassurance: only what all its core products carry; the guarantee always, opening its popup.
    const safe = document.createElement("span");
    const facts = [];
    if (builder.dataset.derm === "true") facts.push(ui.promise_derm);
    if (builder.dataset.allergy === "true") facts.push(ui.promise_allergy);
    if (facts.length) safe.append(facts.join(" · ") + " · ");
    // From markup, so the control has its attributes when it is set up (drawer-control reads them on creation).
    const holder = document.createElement("span");
    holder.innerHTML = '<drawer-control for="satisfaction-guarantee" action="open" role="button" tabindex="0" class="guide__promise-link"></drawer-control>';
    holder.firstChild.textContent = ui.promise_guarantee;
    safe.append(holder.firstChild);
    // The safety line goes under the buy button, where the decision is made; the list keeps the ritual (design review,
    // 2026-10-21: "Udvalgt mod …" repeated the title).
    const footer = builder.querySelector(".routine__footer");
    if (footer) {
      let trust = footer.querySelector(".routine__trust");
      if (!trust) {
        trust = document.createElement("p");
        trust.className = "routine__trust";
        footer.append(trust);
      }
      trust.replaceChildren(...safe.cloneNode(true).childNodes);
      // The ritual with it, in the box with the button (owner, 2026-10-21), not alone under the routine.
      let ritualLine = footer.querySelector(".routine__ritual");
      if (!ritualLine) {
        ritualLine = document.createElement("p");
        ritualLine.className = "routine__trust routine__ritual";
        footer.append(ritualLine);
      }
      ritualLine.textContent = ritual;
    }
    // On a phone the bar at the bottom has no room for them: the safety line and the ritual are a list under the
    // routine there (desktop has them in the box with the button).
    list.replaceChildren(line(safe.cloneNode(true)), line(ritual));
    list.classList.add("guide__promise--phone");
    list.hidden = false;
  }

  // The answers go with the routine into the cart as a hidden line property (_guide), so orders show what led to them.
  tagRoutine(box) {
    const builder = box.querySelector("routine-builder");
    if (builder && this.answersTag) builder.dataset.guideAnswers = this.answersTag;
    this.renderPromise(box);
    const state = this.state();
    if (builder) this.applyPreferences(builder, state);
    if (builder) this.applyChoices(builder, state);
    if (builder && this.savedRoutine) this.applySaved(builder, this.savedRoutine);
    // Hair: "Ja, tit" to heat styling ticks the routine's heat protection in (an extra in most hair routines).
    if (builder && state.g === "haar" && state.heat === "ja" && builder.steps) {
      const heat = builder.steps.find((s) => /varmebeskyttelse/i.test((s.el.querySelector(".routine-step__label") || {}).textContent || ""));
      if (heat && heat.el.classList.contains("is-collapsed")) builder.setExpanded(heat, true, false);
    }
    // Last, so the cart gets what the result shows (heat protection included), as "Læg rutinen i kurven" would; through the
    // cart drawer once it is set up (it opens, as for any add).
    if (builder && this.buyPending) {
      this.buyPending = false;
      customElements.whenDefined("cart-drawer").then(() => this.buySaved(builder, state));
    }
  }

  // Rutine v2 (owner, 2026-10-20, after The Ordinary and Paula's Choice): products to avoid while pregnant (or, for skin
  // that reacts easily, in the extra focus); the further concerns as "Ekstra fokus" steps; Enkel or Komplet; the scent;
  // the line saying what the routine is for; the skin journey. The choices live in the link (niveau, duft) like the answers.
  applyChoices(builder, state) {
    const ui = this.copy.ui;
    const face = state.g === "ansigt" && !state.r;
    const choices = this.result.querySelector("[data-guide-choices]");
    const levelBox = this.result.querySelector("[data-guide-level]");
    const scentBox = this.result.querySelector("[data-guide-scent]");
    const pregnancy = this.result.querySelector("[data-guide-pregnancy]");
    const list = (value) => (value || "").split(",").map((h) => h.trim()).filter(Boolean);
    const avoid = new Set(list(this.dataset.avoid));
    // Skin that reacts easily was promised "uden A-vitamin og syrer": the extra focus skips those, and the acids too.
    const avoidReact = new Set([...avoid, ...list(this.dataset.avoidReact)]);
    const gravid = face && state.gravid === "ja";
    const steps = () => builder.steps || [];
    // A saved routine opened without its pregnancy answer: what to avoid in pregnancy stays out unless it was saved, so a
    // product chosen with "Ja" never comes back (also not in Komplet); the pregnancy note is not shown (nothing is assumed).
    const wanted = face && this.savedRoutine ? this.savedHandles(this.savedRoutine) : null;
    const avoidNow = wanted ? new Set([...avoid].filter((h) => !wanted.has(h))) : avoid;

    // Pregnant: a step with a product to avoid takes another of its products, or leaves the routine.
    if (gravid || wanted) {
      steps().forEach((step) => {
        if (!avoidNow.has(step.el.dataset.handle)) return;
        const alt = [...builder.querySelectorAll(`template[data-routine-alt="${step.el.dataset.stepKey}"]`)].find((t) => !avoidNow.has(t.dataset.handle));
        if (!(alt && builder.swapTo(step.el.dataset.stepKey, alt.dataset.handle))) {
          step.avoided = true;
          builder.setHidden(step, true);
        }
      });
    }
    // Said whenever the answer is yes: the reassurance, whether or not this routine had anything to leave out.
    pregnancy.hidden = !gravid;
    if (gravid) this.result.querySelector("[data-guide-why]").hidden = false;

    // The further concerns (face: 2 and 3; body: 2): their fokus step (body ones are "krop-<concern>"). A product the
    // routine already offers as an add-on is opened instead of added twice; one to avoid is skipped.
    const body = state.g === "krop" && !state.r;
    const concerns = (face || body) && state.concern ? state.concern.split(",") : [];
    const nounOf = (key) => {
      if (face) return (this.copy.for.concern || {})[key] || "";
      const q = this.copy.guides.krop.questions.find((x) => x.id === "concern");
      const o = q && q.options.find((x) => x.value === key);
      return o ? this.optionLabel(q, o).toLowerCase() : "";
    };
    const focus = [];
    // Body: blemishes and pigmentation take the routine's weekly scrub in (the charcoal one), pigmentation its sunscreen too.
    if (body && (concerns[0] === "uren" || concerns[0] === "pigment") && !builder.sunAdded) {
      builder.sunAdded = true;
      const scrub = steps().find((s) => s.el.hasAttribute("data-routine-extra") && /scrub|eksfol/i.test((s.el.dataset.type || "") + " " + (s.el.dataset.handle || "")) && !s.focus);
      if (scrub) scrub.focus = concerns[0];
      if (concerns[0] === "pigment") {
        const sun = steps().find((s) => s.el.hasAttribute("data-routine-extra") && /sol/i.test(s.el.dataset.type || "") && !s.focus);
        if (sun) sun.focus = "pigment";
      }
    }
    if (!builder.focusAdded) {
      builder.focusAdded = true;
      concerns.slice(1).forEach((key) => {
        const skip = state.react === "ja" ? avoidReact : gravid || wanted ? avoidNow : new Set();
        // In order: an add-on the routine already offers is opened; a product already in the routine as a basic step
        // passes to the next; otherwise the first free one is added.
        const tpls = [...builder.querySelectorAll(`template[data-routine-focus="${body ? "krop-" + key : key}"]`)].filter((t) => !skip.has(t.dataset.handle));
        // Body: a need with a routine of its own brings that routine's basic steps, not just one product (owner,
        // 2026-10-23: a self-tanner wants its mitt, and the scrub the day before). A product already here is used (an
        // add-on opened); a kind of step already here (Lotion, Bodyscrub, Vask …) is not added twice. At most six products:
        // the fokus product first, then the steps of use, the preparing ones ("Dagen før") last; in the routine's order.
        const doc = body && this.moreDocs && this.moreDocs[key];
        if (doc) {
          const inUse = (s) => !s.el.hasAttribute("data-routine-extra") || s.focus;
          const cards = [...doc.querySelectorAll(".routine__steps > [data-routine-step]:not([data-routine-extra])")].filter((c) => !skip.has(c.dataset.handle));
          const fokus = new Set(tpls.map((t) => t.dataset.handle));
          const prep = (c) => /før\b/i.test((c.querySelector(".routine-step__when") || {}).textContent || "");
          const ranked = [...cards.filter((c) => fokus.has(c.dataset.handle)), ...cards.filter((c) => !fokus.has(c.dataset.handle) && !prep(c)), ...cards.filter((c) => !fokus.has(c.dataset.handle) && prep(c))];
          // The kind of step: its product type and its step name ("Vask": a shower gel already here, no hand soap too).
          const kinds = (el) => [el.dataset.type, ((el.querySelector(".routine-step__label") || {}).textContent || "").trim()].filter(Boolean);
          const types = new Set(steps().filter(inUse).flatMap((s) => kinds(s.el)));
          const chosen = new Set();
          let brought = 0;
          ranked.forEach((c) => {
            const room = 6 - steps().filter(inUse).length - chosen.size;
            const have = steps().find((s) => s.el.dataset.handle === c.dataset.handle);
            if (have) {
              if (inUse(have)) brought += 1;
              else if (room > 0) {
                have.focus = key;
                brought += 1;
              }
              return;
            }
            if (room <= 0 || kinds(c).some((k) => types.has(k))) return;
            chosen.add(c);
            kinds(c).forEach((k) => types.add(k));
          });
          cards.filter((c) => chosen.has(c)).forEach((c) => {
            const fresh = document.importNode(c, true);
            const label = fresh.querySelector(".routine-step__label");
            const noun = nounOf(key);
            if (label && noun && fokus.has(c.dataset.handle)) label.textContent = fill(ui.focus_label, { concern: noun });
            builder.insertStep(fresh, null).focus = key;
            brought += 1;
          });
          if (brought) return;
        }
        // A face routine already has its day and night cream: a cream for the extra concern would be a third (owner,
        // 2026-10-21: Anti-rødme Creme beside Dagcreme and Natcreme), so the next kind of product is taken instead.
        const typeOf = (t) => (t.content.querySelector("[data-routine-step]") || { dataset: {} }).dataset.type || "";
        const isCream = (type) => /creme/i.test(type) && !/sol/i.test(type);
        const hasCream = face && steps().some((s) => isCream(s.el.dataset.type || "") && !s.el.hasAttribute("data-routine-extra"));
        // Pigment or uneven tone as an extra concern needs sun protection: the day cream with SPF where the routine offers
        // it, else its sunscreen add-on (routine review, 2026-10-21).
        if (face && (key === "pigment" || key === "hudtone")) {
          const spf = /spf|solfaktor|solcreme|sun/i;
          const inView = () => steps().filter((s) => !s.el.hasAttribute("data-routine-extra") || s.focus);
          const hasSpf = () => inView().some((s) => s.el.hasAttribute("data-spf") || spf.test(s.el.dataset.handle || "") || /sol/i.test(s.el.dataset.type || ""));
          if (!hasSpf()) {
            const alt = [...builder.querySelectorAll("template[data-routine-alt]")].find((t) => t.dataset.handle === "dagcreme-spf-20" && t.isConnected);
            if (alt) builder.swapTo(alt.dataset.routineAlt, "dagcreme-spf-20");
          }
          if (!hasSpf()) {
            const sun = steps().find((s) => s.el.hasAttribute("data-routine-extra") && /sol/i.test(s.el.dataset.type || "") && !s.focus);
            if (sun) sun.focus = key;
          }
        }
        // A retinoid in the routine: no daily acid peel on top (the weekly peel mask may stay).
        const retinoid = steps().some((s) => /retinol|vitamin-a/.test(s.el.dataset.handle || "") && (!s.el.hasAttribute("data-routine-extra") || s.focus));
        // And at most two serums: a third concern takes its next kind of product (a mask) instead (owner, 2026-10-21).
        const inRoutine = (s) => !s.el.hasAttribute("data-routine-extra") || s.focus;
        const serumCount = steps().filter((s) => inRoutine(s) && /serum/i.test(s.el.dataset.type || "")).length;
        let pick = null;
        for (const t of tpls) {
          if (hasCream && isCream(typeOf(t))) continue;
          if (retinoid && t.dataset.handle === "daily-peel") continue;
          if (face && serumCount >= 2 && /serum/i.test(typeOf(t))) continue;
          const have = steps().find((s) => s.el.dataset.handle === t.dataset.handle);
          if (!have) { pick = t; break; }
          if (have.el.hasAttribute("data-routine-extra")) { have.focus = key; return; }
        }
        const card = pick && pick.content.querySelector("[data-routine-step]");
        if (!card) return;
        const fresh = card.cloneNode(true);
        const label = fresh.querySelector(".routine-step__label");
        const noun = nounOf(key);
        if (label && noun) label.textContent = fill(ui.focus_label, { concern: noun });
        // In the order of use: a serum right after the routine's serum (before the creams); masks and the rest last.
        const isSerum = /serum/i.test(fresh.dataset.type || "");
        // After the last serum already in (an earlier extra concern's too), so the concerns keep their order.
        const serums = steps().filter((s) => /serum/i.test(s.el.dataset.type || "") && inRoutine(s));
        const step = builder.insertStep(fresh, isSerum && serums.length ? serums[serums.length - 1].el : null);
        step.focus = key;
        // A second serum goes on in the evening, as in Byg din rutine: its tag says Aften.
        const evening = [...builder.querySelectorAll(".routine-step__when")].find((w) => /^aften$/i.test(w.textContent.trim()));
        const tag = fresh.querySelector(".routine-step__when");
        const moon = '<svg aria-hidden="true" viewBox="0 0 20 20" width="12" height="12"><path d="M15.5 12.6A6.5 6.5 0 0 1 7.4 4.5a6.5 6.5 0 1 0 8.1 8.1z" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/></svg>';
        // Vitamin C belongs to the morning (under sun protection): that second serum is tagged Morgen instead.
        const sunIcon = '<svg aria-hidden="true" viewBox="0 0 20 20" width="12" height="12"><circle cx="10" cy="10" r="3.6" fill="none" stroke="currentColor" stroke-width="1.6"/><path d="M10 2.5v2M10 15.5v2M2.5 10h2M15.5 10h2M4.7 4.7l1.4 1.4M13.9 13.9l1.4 1.4M4.7 15.3l1.4-1.4M13.9 6.1l1.4-1.4" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>';
        const vitaminC = /c-vitamin|vitamin-c/.test(pick.dataset.handle || "");
        if (isSerum && serums.length && tag) tag.innerHTML = vitaminC ? sunIcon + ui.when_morning : evening ? evening.innerHTML : moon + ui.when_evening;
      });
    }
    steps().forEach((s) => s.focus && focus.push(s.focus));
    // No separate add-ons in the result: Enkel / Komplet is the one way to make the routine bigger, so the prices on the
    // switch and the total always agree (owner, 2026-10-21). The extra concerns' steps stay (they are Komplet).
    steps().forEach((s) => {
      if (s.el.hasAttribute("data-routine-extra") && !s.focus) {
        s.addon = true;
        builder.setHidden(s, true);
      }
    });

    // Enkel / Komplet: only where the routine marks its Enkel steps (face).
    const simple = steps().filter((s) => s.el.hasAttribute("data-simple") && !s.avoided);
    const basics = steps().filter((s) => !s.el.hasAttribute("data-routine-extra") && !s.avoided);
    const showLevel = face && simple.length > 0 && (basics.length + focus.length) > simple.length;
    // Opens on Enkel; Komplet is the upgrade, with what it adds and costs (owner, 2026-10-21, after the design review).
    const level = showLevel && state.niveau === "komplet" ? "komplet" : "enkel";
    const counts = { enkel: simple.length, komplet: basics.length + focus.length };
    const setLevel = (value) => {
      steps().forEach((step) => {
        if (step.avoided) return;
        if (step.focus) {
          // The extra concerns' steps: in Komplet, absent from Enkel (not offered as add-ons).
          builder.setHidden(step, value !== "komplet");
          if (value === "komplet") builder.setExpanded(step, true, false);
        }
        else if (!step.el.hasAttribute("data-routine-extra")) builder.setHidden(step, value === "enkel" && !step.el.hasAttribute("data-simple"));
      });
      // Each half of the switch says what you get and what it costs (after the routine discount only where checkout
      // gives it: the builder's dealFor, the routine function's rule), so the line under it only has to say what the
      // chosen level is for (owner, 2026-10-21: one sentence tried to say both).
      const priceOf = (list) => builder.dealFor(list).toPay;
      const sets = { enkel: simple, komplet: [...basics, ...steps().filter((s) => s.focus && !s.avoided)] };
      this.levelPrices = () => levelBox.querySelectorAll("[data-guide-level-btn]").forEach((b) => {
        const price = b.querySelector(".guide__switch-price");
        if (price) price.textContent = window.ec.money(Math.round(priceOf(sets[b.dataset.guideLevelBtn])), true);
      });
      levelBox.querySelectorAll("[data-guide-level-btn]").forEach((b) => {
        const key = b.dataset.guideLevelBtn;
        b.setAttribute("aria-pressed", String(key === value));
        const name = document.createElement("span");
        name.textContent = (key === "enkel" ? ui.level_simple : ui.level_full) + " · " + fill(ui.steps_n, { n: counts[key] });
        const price = document.createElement("span");
        price.className = "guide__switch-price";
        price.textContent = window.ec.money(Math.round(priceOf(sets[key])), true);
        b.replaceChildren(name, price);
      });
      const nouns = focus.map((k) => (this.copy.for.concern || {})[k]).filter(Boolean);
      const main = (this.copy.for.concern || {})[concerns[0]] || "";
      // In "Hvorfor denne rutine?": what Enkel is and what Komplet adds, the same whichever is chosen.
      const levelText = this.result.querySelector("[data-guide-level-text]");
      levelText.textContent = nouns.length
        ? fill(ui.level_simple_focus, { n: counts.enkel, main, focus: this.joinList(nouns) })
        : fill(ui.level_simple_text, { n: counts.enkel, main });
      levelText.hidden = false;
      const whyBox = this.result.querySelector("[data-guide-why]");
      if (whyBox) whyBox.hidden = false;
      this.renderFor(state);
    };
    levelBox.hidden = !showLevel;
    if (showLevel) {
      levelBox.querySelectorAll("[data-guide-level-btn]").forEach((b) => {
        b.onclick = () => {
          setLevel(b.dataset.guideLevelBtn);
          this.remember({ niveau: b.dataset.guideLevelBtn });
        };
      });
      setLevel(level);
      // A new scent or size changes prices after the click: the switch's prices follow every update of the total.
      if (!builder.levelPricesHooked) {
        builder.levelPricesHooked = true;
        const update = builder.update.bind(builder);
        builder.update = () => {
          update();
          if (this.levelPrices) this.levelPrices();
        };
      }
    } else {
      this.levelPrices = null;
      // No Enkel/Komplet (body, hair): what the customer chose is simply in the routine.
      steps().forEach((s) => s.focus && s.el.classList.contains("is-collapsed") && builder.setExpanded(s, true, false));
      this.renderFor(state);
    }

    // Fragrance-free or scented, where a step in view offers both.
    const scentGroups = () => steps().filter((s) => !s.el.hidden).map((s) => [s, s.el.querySelector(".routine-chips[data-scent]")]).filter(([, g]) => g && g.querySelectorAll(".routine-chip").length > 1);
    const setScent = (free) => {
      scentGroups().forEach(([step, group]) => {
        const chip = [...group.querySelectorAll(".routine-chip")].find((c) => /parfumefri|fragrance/i.test(c.dataset.value) === free);
        if (!chip || chip.classList.contains("is-active")) return;
        const was = group.querySelector(".routine-chip.is-active");
        builder.pick(step, chip);
        // That scent sold out in this size: the product keeps the one it had, rather than leaving the routine unnoticed.
        if (step.el.classList.contains("is-unavailable") && was) builder.pick(step, was);
      });
      scentBox.querySelectorAll("[data-guide-scent-btn]").forEach((b) => {
        const on = (b.dataset.guideScentBtn === "fri") === free;
        b.setAttribute("aria-pressed", String(on));
        b.classList.toggle("is-active", on);
      });
    };
    // Fragrance-free unless the link says otherwise; the choice is on each product's row now, not a switch for the
    // whole routine (owner, 2026-10-21: "leave it out … default scent free … change on each product").
    scentBox.hidden = true;
    if (scentGroups().length) {
      scentBox.querySelectorAll("[data-guide-scent-btn]").forEach((b) => {
        b.onclick = () => {
          setScent(b.dataset.guideScentBtn === "fri");
          this.remember({ duft: b.dataset.guideScentBtn });
        };
      });
      setScent(state.duft !== "duft");
    }
    choices.hidden = levelBox.hidden && scentBox.hidden;
    // "Din hudrejse": the face's weeks 4–8 name what the first concern looks like then; body and hair have their own
    // timeline; none for gifts, sun cream or self-tan, where weeks of use promise nothing (owner, 2026-10-21).
    const journey = this.result.querySelector("[data-guide-journey]");
    if (journey) {
      const krop = state.g === "krop";
      const haar = state.g === "haar";
      journey.hidden = state.g === "gave" || (krop && ["sol", "selvbruner"].includes(concerns[0]));
      if (!journey.hidden) {
        const pre = krop ? "journey_krop_" : haar ? "journey_haar_" : "journey_";
        const t = (key) => ui[pre + key] || ui["journey_" + key] || "";
        journey.querySelector(".guide__journey-title").textContent = t("title");
        journey.querySelector(".guide__journey-intro").textContent = t("intro");
        journey.querySelectorAll(".guide__journey-stage").forEach((li, i) => {
          li.querySelector(".guide__journey-when").textContent = t(i + 1 + "_when");
          li.querySelector(".guide__journey-stage-title").textContent = t(i + 1 + "_title");
          // Weeks 2–4 by the first concern: the face's by name, hair's per need (owner, 2026-10-22: one text for dry hair and scalp read wrong).
          const byConcern = i === 1 && ((face && ui["journey_2_" + concerns[0]]) || (krop && ui["journey_krop_2_" + concerns[0]]) || (haar && ui["journey_haar_2_" + (state.concern || "").split(",")[0]]));
          li.querySelector("p").textContent = byConcern || t(i + 1 + "_text");
        });
      }
    }
  }

  // What the routine is for: the concerns ranked as the customer tapped them (1, 2, 3), then the skin type.
  renderFor(state) {
    const box = this.result.querySelector("[data-guide-for]");
    const list = box.querySelector("[data-guide-priority]");
    const skin = box.querySelector("[data-guide-skin]");
    const words = this.copy.for || {};
    const face = state.g === "ansigt" && !state.r;
    const guide = this.copy.guides[state.g];
    const concernQ = !state.r && guide && state.g !== "gave" ? guide.questions.find((q) => q.id === "concern") : null;
    const keys = concernQ && state.concern ? state.concern.split(",") : [];
    list.replaceChildren(
      ...keys.map((key, i) => {
        const li = document.createElement("li");
        const n = document.createElement("span");
        n.className = "answer__order";
        n.setAttribute("aria-hidden", "true");
        n.textContent = String(i + 1);
        const option = concernQ && concernQ.options.find((o) => o.value === key);
        li.append(n, option ? this.optionLabel(concernQ, option) : key);
        return li;
      })
    );
    const types = face || state.g === "krop" ? [(words.feel_adj || {})[state.feel], state.react === "ja" ? (words.react_adj || {}).ja : ""].filter(Boolean) : [];
    // Hair: "Krøllet hår · skæl".
    const hair = state.g === "haar" ? [(words.haar_type_adj || {})[state.type], (words.scalp_adj || {})[state.scalp]].filter(Boolean) : [];
    skin.textContent = types.length ? fill(this.copy.ui.skin_line, { list: this.joinList(types) }) : hair.length ? hair.join(" · ").replace(/^./, (c) => c.toUpperCase()) : "";
    if (!types.length && hair.length) {
      skin.hidden = false;
      box.hidden = !keys.length && !hair.length;
      return;
    }
    skin.hidden = !types.length;
    box.hidden = !keys.length && !types.length;
  }

  joinList(items) {
    return items.length > 1 ? items.slice(0, -1).join(", ") + this.copy.ui.list_and + items[items.length - 1] : items[0] || "";
  }

  // A choice on the result (niveau, duft) goes into the link without starting the result over.
  remember(values) {
    if (this.host) {
      Object.assign(this.memory, values);
      return;
    }
    const params = new URLSearchParams(window.location.search);
    Object.entries(values).forEach(([k, v]) => params.set(k, v));
    window.history.replaceState(null, "", window.location.pathname + "?" + params.toString());
  }

  // The answers choose between a step's products where the routine offers several (sections/productpage-routine.liquid):
  // dry or mature skin gets Renseskum, other skin Rensegel; 55+ gets Godnat Creme 50+; oily skin the light Ultralet Solcreme.
  // Fragrance-free versions are already preferred in the guide. A step without that product keeps the routine's own.
  applyPreferences(builder, state) {
    if (state.r || typeof builder.swapTo !== "function") return;
    const a = this.answers(state);
    const swap = (handles) => {
      const templates = [...builder.querySelectorAll("template[data-routine-alt]")];
      handles.forEach((handle) => {
        const tpl = templates.find((t) => t.dataset.handle === handle && t.isConnected);
        if (tpl) builder.swapTo(tpl.dataset.routineAlt, handle);
      });
    };
    // Hair: thick or curly hair gets the hair mask instead of the conditioner, and the scalp routine's conditioner and
    // shampoo follow the hair and the scalp.
    if (state.g === "haar") {
      const prefer = [];
      if (a.type === "krollet" || a.type === "tykt") prefer.push("harkur", "scalp-reset-conditioner");
      if (a.type === "fint" || a.type === "normalt") prefer.push("plejende-balsam");
      if (a.scalp === "tor") prefer.push("multi-shampoo");
      if (a.scalp === "skael") prefer.push("scalp-reset-shampoo");
      // Split ends: the hair mask in the conditioner's place; coloured hair: the caring, colour-protecting pair.
      if (a.concern === "slidt") prefer.push("harkur", "hair-oil");
      if (a.concern === "farvet") prefer.push("plejende-shampoo", "plejende-balsam");
      return swap(prefer);
    }
    // Body: blemishes and pigmentation get the fragrance-free scrub with charcoal and acid.
    if (state.g === "krop") {
      const first = (this.answers(state).concern || "").split(",")[0];
      if (first === "uren" || first === "pigment") swap(["bodycare-scrub"]);
      return;
    }
    if (state.g !== "ansigt") return;
    const mature = a.age === "40" || a.age === "55";
    // Oily or T-zone skin gets the gel at any age; dry or mature skin the mild foam.
    const oily = a.feel === "fedtet" || a.feel === "tzone";
    const prefer = [!oily && (a.feel === "tor" || mature) ? "renseskum" : "rensegel"];
    // Skin Balance's own mild mousse stays first while it sells; the gel is only its stand-in when sold out.
    if (builder.dataset.routine === "skin-balance") prefer.shift();
    // No vitamin A without asking: the firming day cream holds a retinoid (hydroxypinacolone retinoate), so lines, and
    // loss of firmness without a yes to vitamin A (or with reactive skin or a pregnancy), get Dagcreme SPF 20 (routine
    // review, 2026-10-21).
    if (a.concern === "linjer" || (a.concern === "slap" && (a.avit !== "ja" || a.react === "ja" || a.gravid === "ja"))) prefer.push("dagcreme-spf-20");
    if (a.age === "55") prefer.push("godnat-creme");
    if (a.feel === "fedtet" || a.feel === "tzone") prefer.push("daily-sun-shield");
    swap(prefer);
  }

  openPeek(href) {
    const body = this.querySelector("[data-guide-peek-body]");
    const title = this.querySelector("[data-guide-peek-title]");
    // Only the latest request may fill the panel (a quick second product: the slower answer is dropped).
    const ticket = (this.peekTicket = (this.peekTicket || 0) + 1);
    const current = () => ticket === this.peekTicket && this.peek.open;
    body.replaceChildren();
    title.textContent = "";
    this.peek.showModal();
    this.peek.scrollTop = 0;
    this.peek.classList.add("is-loading");
    window.ec
      .sectionFetch(href, "product-peek")
      .then((html) => {
        if (!current()) return;
        const section = new DOMParser().parseFromString(html, "text/html").querySelector(".shopify-section");
        if (!section) throw new Error("no product");
        body.replaceChildren(...section.childNodes);
        // The product's name in the sticky head, so it stays in sight while scrolling.
        const name = body.querySelector("[data-peek-title]");
        title.textContent = name ? name.textContent.trim() : "";
        body.focus({ preventScroll: true });
        this.pinTop(this.peek);
      })
      .catch(() => {
        if (current()) window.location.href = href;
      })
      .finally(() => {
        if (ticket === this.peekTicket) this.peek.classList.remove("is-loading");
      });
  }

  // The drawer opens at its top, packshot first: for a moment it is held there (the gallery and Safari's focus moved
  // it down, the packshot cut off under the head; owner's iPhone, 2026-10-21), until the customer touches or scrolls it.
  pinTop(panel) {
    let touched = false;
    const stop = () => (touched = true);
    panel.addEventListener("touchstart", stop, { once: true, passive: true });
    panel.addEventListener("wheel", stop, { once: true, passive: true });
    const until = Date.now() + 1000;
    const hold = () => {
      if (touched || !panel.open) return;
      panel.scrollTop = 0;
      if (Date.now() < until) requestAnimationFrame(hold);
    };
    hold();
  }

  closePeek() {
    if (this.peek.open) this.peek.close();
  }

  // --- Gem og del. The link holds the answers; "Gem rutine" keeps it in the customer's account (custom.saved_routines). ---

  // The guide's own page (in the panel: the page the panel was loaded from), with these answers.
  resultUrl(state) {
    const base = new URL(this.host ? this.host.dataset.url : window.location.pathname + window.location.search, window.location.origin);
    [...base.searchParams.keys()].forEach((key) => {
      if (key !== "view") base.searchParams.delete(key);
    });
    Object.entries(state).forEach(([key, value]) => base.searchParams.set(key, value));
    return base.pathname + base.search;
  }

  markCurrent(state, title) {
    // The shared link never carries gemt (it marks the customer's own saved routine).
    const shared = { ...state };
    delete shared.gemt;
    this.current = { url: this.resultUrl(shared), title };
    if (!this.saveButton) return;
    // Gift ideas are not a routine of your own.
    window.ec.paintSaved(this.saveButton, this.saveUrl(state), state.g === "gave");
    // Just logged in from "Log ind og gem" (ec.saveRoutine): save this result now, once.
    if (this.saveButton.dataset.loggedIn && !this.saveButton.hidden && state.g !== "gave") {
      // Only the result that asked (the builder's own save, or another result, keeps its marker). Saved once the routine
      // has loaded (loadRoutine), or the save would carry no products.
      if (window.ec.takeSaveAfterLogin && window.ec.takeSaveAfterLogin("guide", this.resultUrl(state))) this.saveAfterLogin = true;
    }
  }

  runSaveAfterLogin() {
    if (!this.saveAfterLogin) return;
    this.saveAfterLogin = false;
    // Only while the result is on screen: after "Ret" (a question instead) nothing is saved behind her back.
    if (this.saveButton && !this.saveButton.hidden && !this.saveButton.closest("[hidden]")) this.saveButton.click();
  }

  // The link that is saved: never the pregnancy answer, nor the vitamin A answer that is only asked when it is "no"
  // (owner, 2026-10-26), nor the marker gemt. Reopened from the customer's saved routines (gemt=1), the result shows
  // without them; reopened any other way, the guide asks them again.
  saveUrl(state) {
    const kept = { ...state };
    delete kept.gravid;
    delete kept.avit;
    delete kept.gemt;
    return this.resultUrl(kept);
  }

  // --- A saved routine opened again (gemt=1 from "Dine gemte rutiner" or the Mit ECOOKING account page). ---

  // The answers of a link, without the marker and the result's own choices, in a fixed order.
  answerKey(params) {
    return [...params]
      .filter(([key]) => !NOT_ANSWERS.has(key) && !UNSAVED_ANSWERS.has(key))
      .map(([key, value]) => key + "=" + value)
      .sort()
      .join("&");
  }

  // The customer's saved guide routine with these answers, only when the link says it is one (gemt=1): a guide run from the
  // start that reaches the same answers still asks the pregnancy question.
  savedMatch(state) {
    if (state.gemt !== "1" || !this.saved.length) return null;
    const key = this.answerKey(Object.entries(state));
    return (
      this.saved.find((saved) => {
        if (!saved || saved.kind === "build" || typeof saved.url !== "string" || !Array.isArray(saved.products)) return false;
        return this.answerKey(new URL(saved.url, window.location.origin).searchParams) === key;
      }) || null
    );
  }

  // Product handle → variant id ("" when none) of a saved routine.
  savedHandles(saved) {
    return new Map(
      saved.products.map((entry) => {
        const [handle, variant] = String(entry).split(".");
        return [handle, variant || ""];
      })
    );
  }

  // [routine handle, branch] for a saved routine. The routines the rules could give for the answers it doesn't keep
  // (pregnancy, vitamin A: each value tried, none assumed; "nej" first, so a routine both answers lead to never gets the
  // pregnancy branch's explanation); the saved routine decides, and with none saved (after a
  // pregnancy answer) the one whose section holds most of the saved products. Null while that is being looked up.
  savedChoice(state, saved) {
    const logic = GUIDE_LOGIC[state.g];
    const missing = [...UNSAVED_ANSWERS].filter((key) => !state[key]);
    const combos = missing.reduce((list, key) => list.flatMap((a) => ["nej", "ja"].map((v) => ({ ...a, [key]: v }))), [this.answers(state)]);
    const options = [];
    combos.forEach((answers) => {
      const option = logic.routine(answers);
      if (option && this.routines[option[0]] && !options.some((o) => o[0] === option[0])) options.push(option);
    });
    if (!options.length) return [null, null];
    const exact = saved.routine && options.find((o) => o[0] === saved.routine);
    if (exact) return exact;
    if (saved.routine && this.routines[saved.routine]) return [saved.routine, options[0][1]];
    if (options.length === 1) return options[0];
    const key = saved.url + "|" + options.map((o) => o[0]).join(",");
    if (this.savedPick && this.savedPick.key === key) return this.savedPick.option;
    // Look the candidates up once (the section fetch is cached), then show the result again.
    const box = this.result.querySelector("[data-guide-routine]");
    delete box.dataset.loaded;
    box.setAttribute("aria-busy", "true");
    const wait = document.createElement("p");
    wait.className = "guide__loading";
    wait.textContent = this.copy.ui.loading;
    box.replaceChildren(wait);
    const ticket = (this.savedTicket = {});
    const wanted = this.savedHandles(saved);
    Promise.all(options.map((o) => window.ec.sectionFetch(this.routines[o[0]].url, "productpage-routine").catch(() => "")))
      .then((pages) => {
        if (ticket !== this.savedTicket) return;
        const score = (html) => {
          const doc = new DOMParser().parseFromString(html, "text/html");
          const handles = new Set([...doc.querySelectorAll("[data-handle]")].map((el) => el.dataset.handle));
          return [...wanted.keys()].filter((h) => handles.has(h)).length;
        };
        const scores = pages.map(score);
        const best = scores.indexOf(Math.max(...scores));
        this.savedPick = { key, option: options[best] };
        this.render();
      });
    return null;
  }

  // "Køb rutinen": the customer's saved routine with these answers (its products and variants, as saved) goes in the cart
  // through the routine's own "Læg rutinen i kurven" (routine-builder add: _routine, so the routine discount applies).
  // Sold-out products stay out; the toast names them.
  buySaved(builder, state) {
    const key = this.answerKey(Object.entries(state));
    const saved =
      this.savedRoutine ||
      this.saved.find((s) => s && s.kind !== "build" && typeof s.url === "string" && Array.isArray(s.products) && this.answerKey(new URL(s.url, window.location.origin).searchParams) === key);
    if (saved && saved !== this.savedRoutine) this.applySaved(builder, saved);
    const wanted = saved ? this.savedHandles(saved) : null;
    const steps = builder.steps || [];
    const soldOut = steps
      .filter((s) => s.el.classList.contains("is-unavailable") && !s.el.hidden && (!wanted || wanted.has(s.el.dataset.handle)))
      .map((s) => ((s.el.querySelector(".routine-step__name") || {}).textContent || s.el.dataset.handle).trim());
    const added = builder.ticked().length > 0;
    const done = added ? builder.add() : Promise.resolve({ ok: false });
    Promise.resolve(done).then((result) => {
      const text = window.ec.soldOutText(this, soldOut, added && (!result || result.ok !== false));
      if (!text) return;
      const toast = this.querySelector("[data-guide-toast]");
      toast.textContent = text;
      toast.hidden = false;
      clearTimeout(this.toastTimer);
      this.toastTimer = setTimeout(() => (toast.hidden = true), 8000);
    });
  }

  // The saved products in the routine: a step whose saved product is another of its products takes that one, and each
  // step shows the saved size or scent. Run after the answers' own choices, so the saved routine is what shows.
  applySaved(builder, saved) {
    if (typeof builder.swapTo !== "function") return;
    const wanted = this.savedHandles(saved);
    (builder.steps || []).forEach((step) => {
      if (step.avoided || wanted.has(step.el.dataset.handle)) return;
      const key = step.el.dataset.stepKey;
      const alt = [...builder.querySelectorAll("template[data-routine-alt]")].find((t) => t.dataset.routineAlt === key && wanted.has(t.dataset.handle));
      if (!alt) return;
      const hidden = step.el.hidden;
      if (builder.swapTo(key, alt.dataset.handle) && hidden) builder.setHidden(step, true);
    });
    (builder.steps || []).forEach((step) => {
      const id = wanted.get(step.el.dataset.handle);
      const variant = id && step.variants.find((v) => String(v.id) === id);
      if (!variant || String(step.el.dataset.variantId) === id) return;
      builder.setChips(step, variant);
      builder.apply(step, variant);
    });
  }

  // What "Gem rutine" keeps: the result's link and title, the area, the routine and the products in view with their
  // variants (for "Fuldend din rutine" in Mit ECOOKING).
  savePayload() {
    if (!this.current) return null;
    const box = this.result.querySelector("[data-guide-routine]");
    const products = [...box.querySelectorAll("[data-routine-step][data-handle]")]
      .filter((step) => !step.closest("[hidden]") && !step.hasAttribute("data-routine-extra"))
      .map((step) => step.dataset.handle + (step.dataset.variantId ? "." + step.dataset.variantId : ""));
    // A title that tells routines apart in the account: "Ansigt: Tør, stram hud og Linjer".
    const state = this.state();
    const guide = this.copy.guides[state.g];
    const concern = guide && guide.questions.find((q) => q.id === "concern");
    const labels = (state.concern || "").split(",").filter(Boolean).map((v) => ((concern && concern.options.find((o) => o.value === v)) || {}).label || v);
    const title = guide && labels.length ? guide.title + ": " + labels.join(", ") : this.current.title;
    // The routine's name is left out after a pregnancy answer: the pregnancy-safe routine would tell it.
    // A saved routine saved again keeps what it saved (no routine name after a pregnancy answer).
    const routineName = this.savedRoutine ? this.savedRoutine.routine || "" : state.gravid === "ja" ? "" : this.currentRoutine || "";
    const routine = { kind: "guide", area: state.g || "", title, url: this.saveUrl(state), routine: routineName, products: [...new Set(products)] };
    // The area's answers as the customer's profile (custom.skin_profile; Mit ECOOKING merges it with the other areas and
    // lets her edit or delete each). Only lasting preferences, never pregnancy or vitamin A.
    const list = (value) => (value ? value.split(",") : []);
    if (state.r) {
      // A routine picked without the questions (herre): no answers, so no profile.
    } else if (state.g === "ansigt") {
      routine.profile = { area: "ansigt", hudtype: state.feel || "", concerns: list(state.concern), sensitiv: state.react || "", duft: state.duft === "duft" ? "duft" : "fri" };
    } else if (state.g === "haar") {
      routine.profile = { area: "haar", concern: state.concern || "", type: state.type || "", scalp: state.scalp || "", heat: state.heat || "" };
    } else if (state.g === "krop") {
      routine.profile = { area: "krop", concerns: list(state.concern), texture: state.texture || "", sensitiv: state.react || "" };
    }
    return routine;
  }

  share() {
    if (!this.current) return;
    const url = new URL(this.current.url, window.location.origin).href;
    if (navigator.share) {
      navigator.share({ title: this.current.title, url }).catch(() => {});
      return;
    }
    const toast = this.querySelector("[data-guide-toast]");
    (navigator.clipboard ? navigator.clipboard.writeText(url) : Promise.reject())
      .then(() => {
        toast.textContent = this.copy.ui.copied;
        toast.hidden = false;
        clearTimeout(this.toastTimer);
        this.toastTimer = setTimeout(() => (toast.hidden = true), 2500);
      })
      .catch(() => window.prompt("", url));
  }

  loadRoutine(routine, box, attempt = 0) {
    const seq = attempt ? this.seq : (this.seq = (this.seq || 0) + 1);
    const ui = this.copy.ui;
    const message = (text, className) => {
      delete box.dataset.loaded;
      box.removeAttribute("aria-busy");
      const p = document.createElement("p");
      p.className = className;
      p.textContent = text;
      box.replaceChildren(p);
    };
    if (!routine) return message(ui.error, "guide__error");
    // Keyed by the answers too: the swaps (applyPreferences) only add, so a corrected answer (Ret) leading to the same
    // routine must load it afresh, or a swap for the old answer stays (e.g. Godnat Creme 50+ after changing the age).
    const key = routine.url + "#" + (this.stateTag || "");
    if (box.dataset.loaded === key) return this.runSaveAfterLogin();
    message(ui.loading, "guide__loading");
    box.setAttribute("aria-busy", "true");
    // Body: a further need led to another routine brings that routine's basic steps (applyChoices), so its section comes
    // along; without it the need keeps its one fokus product.
    const more = this.moreRoutines();
    Promise.all([
      window.ec.sectionFetch(routine.url, "productpage-routine"),
      ...more.map((m) => window.ec.sectionFetch(m.url, "productpage-routine").catch(() => null)),
    ])
      .then(([html, ...others]) => {
        if (seq !== this.seq) return;
        this.moreDocs = Object.fromEntries(more.map((m, i) => [m.key, others[i] ? new DOMParser().parseFromString(others[i], "text/html") : null]));
        const section = new DOMParser().parseFromString(html, "text/html").querySelector(".shopify-section");
        if (!section || !section.querySelector("routine-builder")) throw new Error("no routine");
        // The stylesheet and script tags are already on this page.
        section.querySelectorAll("link[rel='stylesheet'], script[src]").forEach((tag) => tag.remove());
        box.replaceChildren(...section.childNodes);
        box.dataset.loaded = key;
        this.tagRoutine(box);
        box.removeAttribute("aria-busy");
        this.runSaveAfterLogin();
      })
      .catch(() => {
        if (seq !== this.seq) return;
        // A dropped connection or a busy moment at Shopify: two more tries before the message (owner, 2026-10-21: the
        // message showed once for answers that load fine).
        if (attempt < 2) {
          setTimeout(() => {
            if (seq === this.seq) this.loadRoutine(routine, box, attempt + 1);
          }, 1200 * (attempt + 1));
          return;
        }
        message(ui.error, "guide__error");
        const again = document.createElement("button");
        again.type = "button";
        again.className = "txt-btn-underscore-1 guide__retry";
        again.textContent = ui.retry;
        again.addEventListener("click", () => this.loadRoutine(routine, box));
        box.querySelector(".guide__error").append(" ", again);
      });
  }

  // Body: the further needs whose own routine is another than the result's ({ key, handle, url }).
  moreRoutines() {
    const state = this.state();
    if (state.g !== "krop" || state.r || !state.concern) return [];
    const answers = this.answers(state);
    const [first] = GUIDE_LOGIC.krop.routine(answers) || [];
    return state.concern
      .split(",")
      .slice(1)
      .map((key) => ({ key, handle: (GUIDE_LOGIC.krop.routine({ ...answers, concern: key }) || [])[0] }))
      .filter((m) => m.handle && m.handle !== first && this.routines[m.handle])
      .map((m) => ({ ...m, url: this.routines[m.handle].url }));
  }

  // Up to three sets: on sale and in stock, within the budget, best fit first.
  showGifts(state, list, note) {
    const pool = this.querySelector("[data-guide-gift-pool]").content;
    const rate = this.rate();
    const [low, high] = (GIFT_BUDGETS[state.budget] || [0, Infinity]).map((amount) => amount * rate);
    const recipients = state.occasion === "vaertinde" ? [state.til, "kollega"] : [state.til];
    const tema = state.til === "ham" ? "ham" : state.tema;

    // Every set that can be bought and suits the occasion and the recipient's kind (a men's set only for "Ham").
    const sets = [...pool.querySelectorAll(".guide__gift")]
      .map((card) => ({ card, til: card.dataset.til.split(","), tema: card.dataset.tema.split(","), price: Number(card.dataset.price), occasion: card.dataset.occasion }))
      .filter((set) => {
        if (set.card.dataset.available !== "true") return false;
        if (set.card.dataset.handle === "gavekort" || set.card.dataset.handle === "lykkeposen") return false;
        if (set.occasion === "jul" && state.occasion !== "jul") return false;
        if (set.occasion === "ikke-jul" && state.occasion === "jul") return false;
        return state.til === "ham" ? set.til.includes("ham") : !(set.til.length === 1 && set.til[0] === "ham");
      });
    const inBudget = (set) => set.price >= low && set.price <= high;
    const forRecipient = (set) => recipients.some((r) => set.til.includes(r));
    const score = (set) => (forRecipient(set) ? 3 : 0) + (tema && set.tema.includes(tema) ? 2 : 0);
    const best = (list) => list.sort((a, b) => score(b) - score(a) || b.price - a.price).slice(0, 3);
    // How far a set's price is from the budget (0 inside it).
    const distance = (set) => (set.price < low ? low - set.price : set.price > high ? set.price - high : 0);

    // Within the budget for this recipient; else the recipient's sets nearest the budget (said so); else the bag and the gift card.
    let picks = best(sets.filter((set) => inBudget(set) && score(set) > 0));
    let message = "";
    if (!picks.length) {
      const theirs = sets.filter(forRecipient);
      picks = theirs.sort((a, b) => distance(a) - distance(b) || score(b) - score(a)).slice(0, 3);
      if (picks.length) message = this.copy.ui.gift_nearest;
    }
    let cards = picks.map((entry) => entry.card);
    if (!cards.length) {
      cards = ["lykkeposen", "gavekort"].map((handle) => pool.querySelector('.guide__gift[data-handle="' + handle + '"]')).filter(Boolean);
      message = this.copy.ui.gift_none;
    }
    note.textContent = message;
    note.hidden = !message;
    // Sets bought from here say so on the order: source "guide-gift" and the answers (_guide), from the card's own
    // button and from the quick-add panel (assets/quick-add.js reads data-atc-source / data-guide-answers on the list).
    const answers = Object.entries(state).map(([key, value]) => key + "=" + value).join("&");
    list.dataset.atcSource = "guide-gift";
    list.dataset.guideAnswers = answers;
    list.replaceChildren(
      ...cards.map((card) => {
        const copy = card.cloneNode(true);
        copy.querySelectorAll('input[name="properties[_atc_source]"]').forEach((input) => {
          input.value = "guide-gift";
          const tag = document.createElement("input");
          tag.type = "hidden";
          tag.name = "properties[_guide]";
          tag.value = list.dataset.guideAnswers;
          input.after(tag);
        });
        return copy;
      })
    );
    list.hidden = false;
  }
}

// The guide tile in a collection: opens the panel on its guide; without the panel, the link goes to the guide's page.
class GuideTrigger extends HTMLElement {
  connectedCallback() {
    this.addEventListener("click", (event) => {
      const drawer = document.querySelector("guide-drawer");
      if (!drawer || event.metaKey || event.ctrlKey || event.shiftKey) return;
      if (!event.target.closest("a")) return;
      event.preventDefault();
      // From the phone menu: the menu closes, so the panel isn't opened behind it (2026-10-29).
      const nav = this.closest("nav-drawer")?.querySelector("dra-wer#navdrawer");
      if (nav && nav.hasAttribute("open")) nav.closeDrawer();
      // A chip on the tile answers the first question already ("concern=tor").
      const chip = event.target.closest("[data-guide-answer]");
      const answers = {};
      if (chip) {
        const [key, value] = chip.dataset.guideAnswer.split("=");
        answers[key] = value;
      }
      drawer.open(this.dataset.guide, event.target.closest("a"), answers);
    });
  }
}

// The panel: fetches sections/guide.liquid once (Section Rendering API) and starts it on the chosen guide.
class GuideDrawer extends HTMLElement {
  open(guide, opener, answers) {
    this.drawer = this.drawer || this.querySelector("dra-wer");
    this.body = this.body || this.querySelector("[data-guide-drawer-body]");
    // The close button scrolls away with the content instead of staying over it (owner, 2026-10-22): it lives in the
    // panel's scroller, beside it.
    this.closer = this.closer || this.querySelector(".guide-drawer__header");
    this.dataset.start = guide;
    this.startState = { g: guide, ...(answers || {}) };
    this.drawer.openDrawer();
    // From the phone menu, which is closed (and inert) by now: focus goes back to the menu button instead.
    const menuButton = opener && opener.closest("nav-drawer") ? document.querySelector(".header-menu-button drawer-control") : null;
    if (opener) this.drawer.opener = menuButton || opener;
    const flow = this.body.querySelector("guide-flow");
    if (flow) return flow.go(this.startState);
    // A spinner while it loads, no words: "Finder din rutine …" read wrong before any question (owner, 2026-10-22).
    this.body.innerHTML = '<p class="guide-drawer__loading" role="status"><span class="guide-drawer__spinner" aria-hidden="true"></span><span class="visually-hidden">' + this.dataset.loading + "</span></p>";
    window.ec
      .sectionFetch(this.dataset.url, "guide")
      .then((html) => {
        const section = new DOMParser().parseFromString(html, "text/html").querySelector(".shopify-section");
        if (!section || !section.querySelector("guide-flow")) throw new Error("no guide");
        this.body.replaceChildren(this.closer, ...section.childNodes);
        // "Næste" below the scrolling area, as the collection quiz's foot (owner, 2026-10-28: inside it, the pinned row
        // split the scroll in two on a phone); one per panel.
        const inner = this.body.parentElement;
        inner.querySelectorAll(":scope > .guide__foot").forEach((f) => f.remove());
        const foot = this.body.querySelector("[data-guide-foot]");
        if (foot) inner.append(foot);
        // Focus into the loaded panel: the close button had it and lost it when the loading spinner replaced it (review
        // 2026-10-28: focus fell to the page). The question, a heading, takes it with a tabindex.
        if (this.drawer.hasAttribute("open")) {
          requestAnimationFrame(() => {
            const q = this.body.querySelector("[data-guide-question]");
            if (q && !q.hasAttribute("tabindex")) q.setAttribute("tabindex", "-1");
            if (q) q.focus({ preventScroll: true });
            if (!this.drawer.contains(document.activeElement)) this.closer.querySelector(".btn-close")?.focus({ preventScroll: true });
          });
        }
      })
      .catch(() => {
        // Anything wrong: the guide's own page.
        window.location.href = opener ? opener.href : new URL(this.dataset.url, window.location.origin).pathname;
      });
  }

  connectedCallback() {
    // A routine added from the panel opens the cart; the panel steps aside, and comes back on its routine when the cart
    // closes, not straight to the collection (owner, 2026-10-28).
    const cart = document.querySelector("#cartdrawer-drawer");
    if (!cart) return;
    new MutationObserver(() => {
      if (cart.hasAttribute("open") && this.drawer && this.drawer.hasAttribute("open")) {
        // The trigger the panel was opened from, kept for when it closes for good (not the cart's close button).
        this.backOpener = this.drawer.opener;
        this.drawer.opener = null;
        this.drawer.closeDrawer();
        this.backFromCart = true;
      } else if (!cart.hasAttribute("open") && this.backFromCart) {
        this.backFromCart = false;
        this.drawer.openDrawer();
        this.drawer.opener = this.backOpener;
        // Back where the visitor was: the result's title, not the close button.
        requestAnimationFrame(() => this.body.querySelector("[data-guide-result]:not([hidden]) [data-guide-result-title]")?.focus({ preventScroll: true }));
      }
    }).observe(cart, { attributes: true, attributeFilter: ["open"] });
  }

  scrollTop() {
    if (this.body) this.body.scrollTop = 0;
  }
}

if (!customElements.get("guide-flow")) customElements.define("guide-flow", GuideFlow);
if (!customElements.get("guide-trigger")) customElements.define("guide-trigger", GuideTrigger);
if (!customElements.get("guide-drawer")) customElements.define("guide-drawer", GuideDrawer);
